import * as Sentry from "@sentry/react";
import type { Connection, TransactionInstruction, TransactionVersion } from "@solana/web3.js";
import {
  AddressLookupTableAccount,
  PublicKey,
  Transaction,
  TransactionMessage,
  VersionedTransaction,
} from "@solana/web3.js";
import { NATIVE_SOL, WRAPPED_SOL_MINT } from "@stepfinance/common";

import { findOrCreateAccountByMint } from "../../actions";
import { confirmOptions } from "../../constants/transaction";
import type { MultiTxnNotificationStrings } from "../../contexts/language/languageTypes";
import type { Wallet } from "../../types/wallet";
import { generateOneToOneNotificationDescription } from "../notifications";
import { sendAllTransactions, sendTransaction } from "../transactions";
import { sendVersion0Transaction } from "../transactions/sendVersion0Transaction";
import { formatStringWithPlaceholders } from "../utils";

const JUPITER_QUOTE_ENDPOINT = "https://quote-api.jup.ag/v4/quote";
const JUPITER_SWAP_ENDPOINT = "https://quote-api.jup.ag/v4/swap";
// TODO: This address has to be moved to @stepfinance/common but it has been declared here because it needs to be in prod ASAP
const STEP_SWAP_FEE_ADDRESS_FOR_REFERRALS = new PublicKey("5cyyVVBMFmQg1dFXnQpe6iYx818eYtHZpEzuvegVQAmU");
//just a marker address to put in a swap to mark it as ours
const STEP_SWAP_MARKER_ADDRESS = new PublicKey("StepFinancEoffersRewarDoptionsForSwappingGM");

export type JupiterRoute = {
  inAmount: number;
  marketInfos: {
    id: string;
    inAmount: number;
    inputMint: string;
    label: string;
    lpFee: {
      amount: number;
      mint: string;
      pct: number;
    };
    notEnoughLiquidity: boolean;
    outAmount: number;
    outputMint: string;
    platformFee: {
      amount: number;
      mint: string;
      pct: number;
    };
    priceImpactPct: number;
  }[];
  outAmount: number;
  outAmountWithSlippage: number;
  priceImpactPct: number;
};

type SwapTokenDetails = {
  amount: number;
  mint: PublicKey;
  symbol: string;
};

export class JupiterSwapHandler {
  connection: Connection;
  wallet: Wallet;
  route: JupiterRoute;
  transactionVersion: TransactionVersion;
  feeAccountInstruction: TransactionInstruction[];
  feeAccount: PublicKey | null;
  serializedTransactions: any;
  transactions: Transaction[];
  versionedTransaction: VersionedTransaction | null;
  notificationStrings: MultiTxnNotificationStrings;
  inputToken: SwapTokenDetails;
  outputToken: SwapTokenDetails;

  constructor(args: {
    connection: Connection;
    wallet: Wallet;
    route: JupiterRoute;
    transactionVersion: TransactionVersion;
    notificationStrings: MultiTxnNotificationStrings;
    inputToken: SwapTokenDetails;
    outputToken: SwapTokenDetails;
  }) {
    this.connection = args.connection;
    this.wallet = args.wallet;
    this.route = args.route;
    this.transactionVersion = args.transactionVersion;
    this.feeAccountInstruction = [];
    this.feeAccount = null;
    this.transactions = [];
    this.versionedTransaction = null;
    this.notificationStrings = args.notificationStrings;
    this.inputToken = args.inputToken;
    this.outputToken = args.outputToken;
  }

  static async getRoutes(
    inputMint: PublicKey,
    outputMint: PublicKey,
    inputAmount: number,
    slippage: number,
    feeBps: number,
    transactionVersion: TransactionVersion,
    abortController?: AbortController,
  ): Promise<JupiterRoute[]> {
    try {
      inputMint = inputMint.equals(NATIVE_SOL) ? WRAPPED_SOL_MINT : inputMint;
      outputMint = outputMint.equals(NATIVE_SOL) ? WRAPPED_SOL_MINT : outputMint;
      const params = new URLSearchParams({
        inputMint: inputMint.toString(),
        outputMint: outputMint.toString(),
        amount: inputAmount.toString(),
        slippage: (slippage * 100).toString(),
        feeBps: feeBps.toString(),
        asLegacyTransaction: (transactionVersion === "legacy").toString(),
      });
      const resp = await fetch(`${JUPITER_QUOTE_ENDPOINT}?${params.toString()}`, { signal: abortController?.signal });

      if (resp.ok && !abortController?.signal.aborted) {
        const data = await resp.json();
        const jupRoutes: JupiterRoute[] = data?.data ?? [];
        return jupRoutes;
      }
    } catch (err: any) {
      if (err.name !== "AbortError") {
        console.error(err);
      }
    }

    return [];
  }

  async setUpFeeCollectionTokenAccount() {
    this.feeAccount = await findOrCreateAccountByMint(
      this.connection,
      this.wallet.publicKey,
      STEP_SWAP_FEE_ADDRESS_FOR_REFERRALS,
      this.feeAccountInstruction,
      this.outputToken.mint.equals(NATIVE_SOL) ? WRAPPED_SOL_MINT : this.outputToken.mint,
    );
  }

  async fetchJupiterTransaction() {
    this.serializedTransactions = await (
      await fetch(JUPITER_SWAP_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          route: this.route,
          userPublicKey: this.wallet.publicKey.toBase58(),
          wrapUnwrapSOL: true,
          // This is the ATA account for the output token where the fee will be sent to.
          feeAccount: this.feeAccount!.toBase58(),
          asLegacyTransaction: this.transactionVersion === "legacy",
        }),
      })
    ).json();
  }

  addFeeAccountTxLegacy() {
    const { setupTransaction, swapTransaction, cleanupTransaction } = this.serializedTransactions;
    if (this.feeAccountInstruction.length) {
      const feeAccountCreateTx = new Transaction();
      feeAccountCreateTx.add(...this.feeAccountInstruction);
      this.transactions.push(feeAccountCreateTx);
    }

    for (const serializedTransaction of [setupTransaction, swapTransaction, cleanupTransaction].filter(Boolean)) {
      this.transactions.push(Transaction.from(Buffer.from(serializedTransaction, "base64")));
    }
  }

  deserializeVersion0Transaction() {
    const { swapTransaction } = this.serializedTransactions;
    const swapTxBuffer = Buffer.from(swapTransaction, "base64");
    this.versionedTransaction = VersionedTransaction.deserialize(swapTxBuffer);
  }

  async addFeeAccountTxVersion0() {
    if (this.feeAccountInstruction.length && this.versionedTransaction) {
      const addressLookupTableAccounts = await Promise.all(
        this.versionedTransaction.message.addressTableLookups.map(async (lookup) => {
          return new AddressLookupTableAccount({
            key: lookup.accountKey,
            state: AddressLookupTableAccount.deserialize(
              await this.connection.getAccountInfo(lookup.accountKey).then((res) => res!.data),
            ),
          });
        }),
      );
      const message = TransactionMessage.decompile(this.versionedTransaction.message, { addressLookupTableAccounts });
      message.instructions.unshift(this.feeAccountInstruction[0]);
      this.versionedTransaction.message = message.compileToV0Message(addressLookupTableAccounts);
    }
  }

  //we want to add an extra account to every swap transaction to identify it as a swap done on step.
  //Here the tx is on the serializedTransactions property - it is the best way to do it
  //for consistency in code. This means deserializing and reserializing the tx, but it's not a big deal
  addStepSwapMarkerAccount() {
    //deser the tx, add the marker account, ser the tx
    const { swapTransaction } = this.serializedTransactions;
    const swapTxBuffer = Buffer.from(swapTransaction, "base64");
    if (this.transactionVersion === "legacy") {
      //legacy txs require us to rebuild a new tx obj based on the other with an acct key added
      let swapTx = Transaction.from(swapTxBuffer);
      const txSizeBefore = swapTx.serialize({ requireAllSignatures: false, verifySignatures: false }).length;
      if (txSizeBefore > 1200) {
        const msg = `Cannot add swap marker. tx is already ${txSizeBefore} bytes long.`;
        console.error(msg);
        Sentry.captureMessage(msg);
      } else {
        const msg = swapTx.compileMessage();
        msg.staticAccountKeys.push(STEP_SWAP_MARKER_ADDRESS);
        msg.header.numReadonlyUnsignedAccounts++;
        swapTx = Transaction.populate(msg);
        //put it back where we got it from
        this.serializedTransactions.swapTransaction = swapTx
          .serialize({
            requireAllSignatures: false,
            verifySignatures: false,
          })
          .toString("base64");
      }
    } else {
      //versioned txs let us push a key directly, but then we have to repair the ixs
      const swapTx = VersionedTransaction.deserialize(Buffer.from(swapTransaction, "base64"));
      const txSizeBefore = swapTx.serialize().length;
      if (txSizeBefore > 1200) {
        const msg = `Cannot add swap marker. tx is already ${txSizeBefore} bytes long.`;
        console.error(msg);
        Sentry.captureMessage(msg);
      } else {
        const lastStaticKeyIndex = swapTx.message.staticAccountKeys.length - 1;
        swapTx.message.staticAccountKeys.push(STEP_SWAP_MARKER_ADDRESS);
        //since we pushed into the static account keys, we need to increment any index into
        //an account in the address lookup table by 1
        swapTx.message.compiledInstructions.forEach((ix) => {
          ix.accountKeyIndexes = ix.accountKeyIndexes.map((index) => (index > lastStaticKeyIndex ? index + 1 : index));
          ix.programIdIndex = ix.programIdIndex > lastStaticKeyIndex ? ix.programIdIndex + 1 : ix.programIdIndex;
        });
        //put it back where we got it from
        this.serializedTransactions.swapTransaction = Buffer.from(swapTx.serialize()).toString("base64");
      }
    }
  }

  async executeVersion0Transaction() {
    await this.setUpFeeCollectionTokenAccount();
    await this.fetchJupiterTransaction();
    this.addStepSwapMarkerAccount();
    this.deserializeVersion0Transaction();
    await this.addFeeAccountTxVersion0();
    if (this.versionedTransaction) {
      await sendVersion0Transaction(
        this.connection,
        this.wallet,
        this.versionedTransaction,
        true,
        confirmOptions,
        true,
        {
          approveMessage: this.notificationStrings.approveTransaction,
          inProgress: {
            message: formatStringWithPlaceholders(
              this.notificationStrings.currentlySwapping.message,
              this.inputToken.symbol,
              this.outputToken.symbol,
            ),
            description: this.notificationStrings.transactionInProgress,
          },
          success: {
            message: formatStringWithPlaceholders(
              this.notificationStrings.finishedSwap.message,
              this.inputToken.symbol,
              this.outputToken.symbol,
            ),
            description: generateOneToOneNotificationDescription({
              tokenA: {
                action: this.notificationStrings.tokenBalanceChanges.paid,
                amount: this.inputToken.amount,
                mint: this.inputToken.mint,
                type: "decrease",
              },
              tokenB: {
                action: this.notificationStrings.tokenBalanceChanges.received,
                amount: this.outputToken.amount,
                mint: this.outputToken.mint,
                type: "increase",
              },
            }),
          },
        },
      );
    } else {
      throw new Error(`Unexpected error: could not get swap transaction for route (${JSON.stringify(this.route)})`);
    }
  }

  async executeLegacyTransaction() {
    await this.setUpFeeCollectionTokenAccount();
    await this.fetchJupiterTransaction();
    this.addStepSwapMarkerAccount();
    this.addFeeAccountTxLegacy();
    if (this.transactions.length > 1) {
      await sendAllTransactions(
        this.connection,
        this.wallet,
        this.transactions,
        [],
        {
          txnPendingMessage: formatStringWithPlaceholders(
            this.notificationStrings.currentlySwapping.message,
            this.inputToken.symbol,
            this.outputToken.symbol,
          ),
          txnSuccessMessage: formatStringWithPlaceholders(
            this.notificationStrings.finishedSwap.message,
            this.inputToken.symbol,
            this.outputToken.symbol,
          ),
        },
        undefined,
        this.notificationStrings,
      );
    } else if (this.transactions.length === 1) {
      //strange to take a whole different code path here, but
      //the notification stuff is slightly different so we need to
      //work in the marker account a slightly different way
      await sendTransaction(
        this.connection,
        this.wallet,
        this.transactions[0].instructions,
        [],
        true,
        confirmOptions,
        true,
        {
          approveMessage: this.notificationStrings.approveTransaction,
          inProgress: {
            message: formatStringWithPlaceholders(
              this.notificationStrings.currentlySwapping.message,
              this.inputToken.symbol,
              this.outputToken.symbol,
            ),
            description: this.notificationStrings.transactionInProgress,
          },
          success: {
            message: formatStringWithPlaceholders(
              this.notificationStrings.finishedSwap.message,
              this.inputToken.symbol,
              this.outputToken.symbol,
            ),
            description: generateOneToOneNotificationDescription({
              tokenA: {
                action: this.notificationStrings.tokenBalanceChanges.paid,
                amount: this.inputToken.amount,
                mint: this.inputToken.mint,
                type: "decrease",
              },
              tokenB: {
                action: this.notificationStrings.tokenBalanceChanges.received,
                amount: this.outputToken.amount,
                mint: this.outputToken.mint,
                type: "increase",
              },
            }),
          },
        },
        //undefined will use defaults per https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Functions/Default_parameters#passing_undefined_vs._other_falsy_values
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        //send in the marker address to be added to the tx being built
        [STEP_SWAP_MARKER_ADDRESS],
      );
    } else {
      throw new Error(`Unexpected error: swap transaction length is zero for route (${JSON.stringify(this.route)})`);
    }
  }
}
